import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const EMPTY = { name: "", gstin: "", pan: "", address: "", city: "", state: "", pincode: "", contact_person: "", email: "", mobile: "", portal_enabled: false, password: "" };

export function AdminVendors({ token, onUnauthorized }) {
  const [vendors, setVendors] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | vendor object
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/vendors`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) return onUnauthorized();
    setVendors(await r.json());
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      const isNew = editing === "new";
      const url = isNew ? `${API_BASE}/api/admin/vendors` : `${API_BASE}/api/admin/vendors/${editing.id}`;
      const method = isNew ? "POST" : "PUT";
      const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      setEditing(null);
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!confirm(`Delete vendor "${v.name}"?`)) return;
    const r = await fetch(`${API_BASE}/api/admin/vendors/${v.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) load();
  };

  const filtered = (vendors || []).filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (v.name + " " + v.gstin + " " + v.city + " " + v.state).toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-vendors">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, GSTIN, city…" className="pl-9" data-testid="vendor-search" />
        </div>
        <Button onClick={() => setEditing("new")} data-testid="vendor-new-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
          <Plus className="h-4 w-4 mr-1" /> New Vendor
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="vendors-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">GSTIN</th>
              <th className="px-4 py-3 font-semibold">City / State</th>
              <th className="px-4 py-3 font-semibold">Contact</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!vendors && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {vendors && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No vendors yet. Add your first vendor to get started.</td></tr>}
            {filtered.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-semibold text-[#0a2a5e]">
                  {v.name}
                  {v.portal_enabled && <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded" title="Portal enabled">Portal</span>}
                </td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{v.gstin || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{[v.city, v.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">
                  {v.email && <div>{v.email}</div>}
                  {v.mobile && <div className="text-slate-500">{v.mobile}</div>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(v)} data-testid={`vendor-edit-${v.id}`} className="text-sky-600 hover:text-sky-800 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(v)} data-testid={`vendor-del-${v.id}`} className="text-red-500 hover:text-red-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <VendorForm initial={editing === "new" ? EMPTY : editing} onCancel={() => setEditing(null)} onSave={save} saving={saving} />}
    </div>
  );
}

function VendorForm({ initial, onCancel, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); if (!form.name.trim()) return alert("Name is required"); onSave(form); };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="vendor-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-[#0a2a5e]">{initial.id ? "Edit Vendor" : "New Vendor"}</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Vendor Name *</label>
            <Input value={form.name} onChange={set("name")} required data-testid="vendor-name-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">GSTIN</label>
              <Input value={form.gstin} onChange={set("gstin")} placeholder="15 chars" data-testid="vendor-gstin-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">PAN</label>
              <Input value={form.pan} onChange={set("pan")} placeholder="10 chars" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Address</label>
            <Input value={form.address} onChange={set("address")} />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">City</label>
              <Input value={form.city} onChange={set("city")} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">State</label>
              <Input value={form.state} onChange={set("state")} placeholder="e.g. West Bengal" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Pincode</label>
              <Input value={form.pincode} onChange={set("pincode")} />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Contact Person</label>
              <Input value={form.contact_person} onChange={set("contact_person")} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
              <Input type="email" value={form.email} onChange={set("email")} data-testid="vendor-email-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Mobile</label>
              <Input value={form.mobile} onChange={set("mobile")} />
            </div>
          </div>
          <div className="pt-3 mt-3 border-t border-slate-200">
            <div className="text-xs font-bold uppercase tracking-wider text-[#0a2a5e] mb-2">Vendor Portal Access</div>
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="portal-enabled"
                checked={!!form.portal_enabled}
                onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })}
                data-testid="vendor-portal-toggle"
                className="h-4 w-4 rounded border-slate-300 text-[#0a2a5e] focus:ring-[#0a2a5e]"
              />
              <label htmlFor="portal-enabled" className="text-sm text-slate-700">Enable vendor portal login for this vendor</label>
            </div>
            {form.portal_enabled && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  {initial.id ? "New Password (leave blank to keep existing)" : "Password *"}
                </label>
                <Input
                  type="text"
                  value={form.password}
                  onChange={set("password")}
                  data-testid="vendor-password-input"
                  placeholder="Vendor will use email + this password to login at /vendor"
                />
                <p className="text-xs text-slate-400 mt-1">Share this password with the vendor manually (WhatsApp / email).</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="vendor-save-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
              {saving ? "Saving…" : "Save Vendor"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
